// Page - Task List (Refactored to match task_list_preview.html)
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Filter, MoreHorizontal, ChevronLeft, ChevronRight, PlusSquare, Search, Loader2, CheckCircle2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

const StatusBadge = ({ status }) => {
  switch (status) {
    case 'GENERATED':
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-[#ebf6db] text-[#5f8b1e]">
          <CheckCircle2 size={12} className="text-[#5f8b1e]" />
          已生成
        </span>
      );
    case 'GENERATING':
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-blue-50 text-blue-600">
          <Loader2 size={12} className="animate-spin" />
          待生成
        </span>
      );
    case 'COMPLETED':
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-[#ebf6db] text-[#5f8b1e]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#8cc13f]"></span>
          已完成
        </span>
      );
    case 'CONTENT_READY':
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-purple-50 text-purple-600">
          <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
          待筛选
        </span>
      );
    case 'CRAWLING':
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-yellow-50 text-yellow-600">
          <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
          抓取中
        </span>
      );
    case 'PROCESSING':
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-[#fef0e6] text-[#d95316]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#fc773c]"></span>
          处理中
        </span>
      );
    case 'PENDING':
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 bg-gray-100 text-gray-600">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
          待处理
        </span>
      );
    default:
      return null;
  }
};

const PriorityBadge = ({ level }) => {
  const style = level === 'High'
    ? { color: '#fc773c', backgroundColor: 'rgba(244, 152, 111, 0.2)' }
    : { color: '#256df3', backgroundColor: 'rgba(37, 109, 243, 0.1)' };

  return (
    <span className="text-xs font-bold px-2 py-0.5 rounded" style={style}>
      {level === 'High' ? '高' : '普通'}
    </span>
  );
};

export default function SeedTasks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedRows, setSelectedRows] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewScope, setViewScope] = useState(() => localStorage.getItem('globalAdminViewScope') || 'mine');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const itemsPerPage = 10;

  useEffect(() => {
    localStorage.setItem('globalAdminViewScope', viewScope);
    if (user) fetchTasks();
  }, [viewScope, user]);

  // 真实轮询：每5秒刷新一次列表，检查状态更新
  useEffect(() => {
    const interval = setInterval(() => {
      // 只有当有正在进行的任务时才轮询，或者简单点每5秒刷一次
      const hasPending = tasks.some(t => ['CRAWLING', 'PROCESSING', 'GENERATING'].includes(t.status));
      if (hasPending) {
        fetchTasks();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [tasks, user, viewScope]); // Add fetchTasks dependencies

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API_URL}/tasks`, { headers: { 'x-user-email': user.email }, params: { view_scope: viewScope } });
      setTasks(res.data);
    } catch (err) {
      console.error("Failed to fetch tasks", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (task.type !== 'CRAWL_GENERATE' && task.type !== 'IMPORT') return false;
    const searchMatch = String(task.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    let statusMatch = true;
    if (statusFilter === 'IN_PROGRESS') {
      statusMatch = ['CRAWLING', 'PROCESSING', 'CONTENT_READY', 'GENERATING'].includes(task.status);
    } else if (statusFilter === 'COMPLETED') {
      statusMatch = ['COMPLETED', 'GENERATED'].includes(task.status);
    }

    return searchMatch && statusMatch;
  });

  const getStatusWeight = (status) => {
    switch (status) {
      case 'FAILED':
      case 'ERROR':
        return 0;
      case 'CONTENT_READY':
        return 1;
      case 'GENERATING':
      case 'READY_TO_GENERATE':
        return 2;
      case 'CRAWLING':
      case 'PROCESSING':
        return 3;
      case 'COMPLETED':
      case 'GENERATED':
        return 4;
      default:
        return 5;
    }
  };

  const sortedFilteredTasks = [...filteredTasks].sort((a, b) => {
    const weightA = getStatusWeight(a.status);
    const weightB = getStatusWeight(b.status);
    if (weightA !== weightB) {
      return weightA - weightB;
    }
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });

  const totalPages = Math.ceil(sortedFilteredTasks.length / itemsPerPage) || 1;
  const paginatedTasks = sortedFilteredTasks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [sortedFilteredTasks.length, currentPage, totalPages]);

  const toggleRow = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter(rowId => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const toggleAll = () => {
    const currentPageTaskIds = paginatedTasks.map(t => t.id);
    const allSelected = currentPageTaskIds.every(id => selectedRows.includes(id));
    if (allSelected) {
      setSelectedRows(selectedRows.filter(id => !currentPageTaskIds.includes(id)));
    } else {
      const newSelected = new Set([...selectedRows, ...currentPageTaskIds]);
      setSelectedRows(Array.from(newSelected));
    }
  };

  const handleTaskClick = (task) => {
    if (task.status === 'CRAWLING' || task.status === 'PROCESSING') {
        // 禁止点击
        return;
    }
    // 无论是已生成、生成中还是待挑选，只要有了素材，都进入素材挑选列表
    navigate(`/tasks/${task.id}/content?from=seed-tasks`);
  };

  const handleDeleteTask = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("确定要删除这个任务吗？")) return;
    try {
      await axios.delete(`${API_URL}/tasks/${id}`, {
        headers: { 'x-user-email': user.email }
      });
      const newTasks = tasks.filter(t => t.id !== id);
      setTasks(newTasks);
      const newTotalPages = Math.ceil(newTasks.length / itemsPerPage) || 1;
      if (currentPage > newTotalPages) {
        setCurrentPage(newTotalPages);
      }
    } catch (err) {
      console.error("Failed to delete task", err);
      alert("删除任务失败");
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center justify-between mb-6 sticky top-0 z-30 pt-4 pb-2 bg-gray-50/95 backdrop-blur-sm px-1 gap-4 md:gap-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">素材/种子管理</h1>
          <p className="text-gray-500 text-sm mt-1">管理通过在线抓取和Excel导入的原始数据池</p>
        </div>

        
        <div className="flex items-center justify-between md:justify-end space-x-4 w-full md:w-auto">
          {user?.role === 'ADMIN' && (
            <div className="flex bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewScope('mine')}
                className={`px-4 py-1.5 text-sm rounded-md transition-all ${viewScope === 'mine' ? 'bg-white text-blue-600 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'}`}
              >
                我的任务
              </button>
              <button
                onClick={() => setViewScope('all')}
                className={`px-4 py-1.5 text-sm rounded-md transition-all ${viewScope === 'all' ? 'bg-white text-blue-600 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'}`}
              >
                全部任务
              </button>
            </div>
          )}

          <div className="flex items-center bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-300 transition-all w-full md:w-64">
            <Search size={18} className="text-gray-400" />
            <input
              type="text"
              placeholder="搜索任务..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="bg-transparent border-none outline-none text-sm ml-2 w-full placeholder-gray-400 text-gray-700"
            />
          </div>

          <button
            onClick={() => navigate('/new-project')}
            className="hidden md:flex px-4 py-2 bg-gradient-to-r from-[#256df3] to-[#37a7f3] text-white rounded-xl shadow-lg shadow-blue-200 text-sm font-medium hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 items-center space-x-2"
          >
            <PlusSquare size={16} />
            <span>新建</span>
          </button>
        </div>
      </header>

      {/* Task Table */}
      <div className="bg-white border border-gray-100 rounded-3xl shadow-[0_2px_10px_-2px_rgba(0,0,0,0.05)] flex flex-col flex-1 overflow-hidden">
        {/* Filters Toolbar */}
        <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center space-x-2 overflow-x-auto w-full sm:w-auto pb-2 sm:pb-0">
            <button className="flex items-center space-x-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm font-medium text-gray-700 transition-colors border border-gray-200">
              <Filter size={16} />
              <span>筛选</span>
            </button>
            <div className="h-6 w-px bg-gray-200 mx-2"></div>
            <button 
              onClick={() => setStatusFilter('ALL')}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${statusFilter === 'ALL' ? 'bg-blue-50 text-[#256df3]' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              全部
            </button>
            <button 
              onClick={() => setStatusFilter('IN_PROGRESS')}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${statusFilter === 'IN_PROGRESS' ? 'bg-blue-50 text-[#256df3]' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              进行中
            </button>
            <button 
              onClick={() => setStatusFilter('COMPLETED')}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${statusFilter === 'COMPLETED' ? 'bg-blue-50 text-[#256df3]' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              已完成
            </button>
          </div>

          <div className="flex items-center space-x-3 text-sm text-gray-500">
            <span>共 {sortedFilteredTasks.length} 个任务</span>
            <button className="p-1.5 hover:bg-gray-100 rounded-md transition-colors">
              <MoreHorizontal size={20} />
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto flex-1 custom-scrollbar">
          {loading ? (
             <div className="p-8 text-center text-gray-500">加载任务中...</div>
          ) : (
          <table className="w-full text-left border-collapse min-w-[900px]">
            <thead>
              <tr className="text-xs font-semibold text-gray-500 bg-gray-50/50 border-b border-gray-100">
                <th className="py-4 pl-6 w-12">
                  <input
                    type="checkbox"
                    className="custom-checkbox accent-[#256df3]"
                    checked={paginatedTasks.length > 0 && paginatedTasks.every(t => selectedRows.includes(t.id))}
                    onChange={toggleAll}
                  />
                </th>
                <th className="py-4 pl-2">任务名称</th>
                <th className="py-4">负责人</th>
                <th className="py-4">状态</th>
                <th className="py-4">创建时间</th>
                <th className="py-4">来源</th>
                <th className="py-4 pr-6 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {paginatedTasks.map((task) => (
                <tr
                  key={task.id}
                  className={`group border-b border-gray-50 last:border-none transition-colors ${(task.status === 'CRAWLING' || task.status === 'PROCESSING') ? 'opacity-60 cursor-not-allowed bg-gray-50/50' : 'cursor-pointer ' + (selectedRows.includes(task.id) ? 'bg-blue-50/30' : 'hover:bg-gray-50')}`}
                  onClick={() => handleTaskClick(task)}
                >
                  <td className="py-4 pl-6" onClick={(e) => { e.stopPropagation(); toggleRow(task.id); }}>
                    <input
                      type="checkbox"
                      className="custom-checkbox accent-[#256df3]"
                      checked={selectedRows.includes(task.id)}
                      onChange={() => { }}
                    />
                  </td>
                  <td className="py-4 pl-2 font-medium text-slate-700">
                    {task.type === 'CRAWL_GENERATE' && <span className="mr-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-600 border border-blue-200">[在线抓取]</span>}
                    {task.type === 'IMPORT' && <span className="mr-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-600 border border-emerald-200">[Excel导入]</span>}
                    {task.name || '未命名任务'} <span className="text-gray-400 text-xs font-normal">#{task.id}</span>
                  </td>
                  <td className="py-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[10px] font-bold text-gray-500 shadow-sm">
                        {task.user?.name?.[0] || 'U'}
                      </div>
                      <span className="text-gray-600">{task.user?.name || 'User'}</span>
                    </div>
                  </td>
                  <td className="py-4">
                    <StatusBadge status={task.status} />
                  </td>
                  <td className="py-4 text-gray-500 font-mono text-xs">
                    {new Date(task.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-4 text-xs text-gray-500">
                    <div className="max-w-[150px] truncate" title={task.crawlType === 'keyword' ? `搜: ${task.crawlValue}` : (task.crawlType === 'author' ? '达人主页' : '模板生成')}>
                      {task.crawlType === 'keyword' ? `搜: ${task.crawlValue}` : (task.crawlType === 'author' ? '达人主页' : '模板生成')}
                    </div>
                  </td>
                  <td className="py-4 pr-6 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="text-gray-400 hover:text-[#256df3] font-medium text-xs mr-3" onClick={(e) => { e.stopPropagation(); setSelectedTask(task); }}>详情</button>
                    <button className="text-gray-400 hover:text-red-500 font-medium text-xs" onClick={(e) => handleDeleteTask(e, task.id)}>删除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-gray-100 flex justify-between items-center bg-white">
          <div className="text-xs text-gray-500">
            显示 {tasks.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} - {Math.min(currentPage * itemsPerPage, tasks.length)} 条，共 {tasks.length} 条记录
          </div>
          <div className="flex items-center space-x-2">
            <button 
              className="p-2 border border-gray-200 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            >
              <ChevronLeft size={16} />
            </button>
            <button className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#256df3] text-white text-sm font-medium shadow-md shadow-blue-200">
              {currentPage}
            </button>
            <button 
              className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Task Details Modal */}
      {selectedTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">任务详情</h3>
              <button onClick={() => setSelectedTask(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4 text-sm text-gray-700">
              <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-4">
                <div className="col-span-1 text-gray-500 font-medium">任务名称</div>
                <div className="col-span-2 font-semibold text-gray-900">{selectedTask.name || '未命名'}</div>
              </div>

              <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-4">
                <div className="col-span-1 text-gray-500 font-medium">任务类型</div>
                <div className="col-span-2">
                  {selectedTask.type === 'CRAWL_GENERATE' ? '抓取生成' : '文本生成'}
                </div>
              </div>

              {selectedTask.type === 'CRAWL_GENERATE' && (
                <>
                  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-4">
                    <div className="col-span-1 text-gray-500 font-medium">抓取方式</div>
                    <div className="col-span-2">
                      {selectedTask.crawlType === 'keyword' ? '关键词搜索' : '达人主页'}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-4">
                    <div className="col-span-1 text-gray-500 font-medium">抓取目标</div>
                    <div className="col-span-2 text-[#256df3] bg-blue-50 px-2 py-1 rounded inline-block">
                      {selectedTask.crawlValue || '无'}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-4">
                    <div className="col-span-1 text-gray-500 font-medium">抓取数量</div>
                    <div className="col-span-2">
                      {selectedTask.crawlCount || 0} 篇
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-4">
                    <div className="col-span-1 text-gray-500 font-medium">排序方式</div>
                    <div className="col-span-2">
                      {selectedTask.crawlSort === 'time_desc' ? '最新发布' : (selectedTask.crawlSort === 'general' ? '综合排序' : selectedTask.crawlSort || '默认')}
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-4">
                <div className="col-span-1 text-gray-500 font-medium">使用模板</div>
                <div className="col-span-2 font-medium">
                  {selectedTask.template?.name || '默认结构'}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-4">
                <div className="col-span-1 text-gray-500 font-medium">生成数量</div>
                <div className="col-span-2">
                  {selectedTask.genCount || 1} 篇
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-1 text-gray-500 font-medium">字数要求</div>
                <div className="col-span-2">
                  {selectedTask.genWordCount || '默认'}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 border-t border-gray-100 flex justify-end">
              <button 
                onClick={() => setSelectedTask(null)}
                className="px-5 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm font-medium text-sm"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
